import type { TDealTasksData } from '@crm/entities/deal';
import type { Employee, Project, Roles, Tabs, TasksInformationSchema } from '@entities/task';

type TDealEmployee = TDealTasksData['employees'][number];
type TDealProject = TDealTasksData['projects'][number];
type TDealRoles = TDealTasksData['roles'];
type TDealTabs = TDealTasksData['tabs'];

const DEFAULT_TABS: Tabs = {
    board: {
        outdated: [],
        'in_progress': [],
        today: [],
        'seven_days': [],
        'no_term': [],
    },
    list: {},
};

const DEFAULT_ROLES: Roles = {
    author: {
        'can_update': false,
        'can_delete': false,
        'can_complete': false,
    },
    executor: {
        'can_update': false,
        'can_delete': false,
        'can_complete': false,
    },
    observer: {
        'can_update': false,
        'can_delete': false,
        'can_complete': false,
    },
};

const mapEmployee = (employee: TDealEmployee): Employee => ({
    id: employee.id,
    name: employee.name,
    'avatar_url': employee.avatarUrl,
    'current_user': employee.currentUser,
});

const mapProject = (project: TDealProject): Project => ({
    id: project.id,
    name: project.name,
    estimates: project.estimates,
});

const mapRoles = (roles?: TDealRoles): Roles => {
    if (!roles) {
        return DEFAULT_ROLES;
    }

    return {
        author: {
            'can_update': roles.author.canUpdate,
            'can_delete': roles.author.canDelete,
            'can_complete': roles.author.canComplete,
        },
        executor: {
            'can_update': roles.executor.canUpdate,
            'can_delete': roles.executor.canDelete,
            'can_complete': roles.executor.canComplete,
        },
        observer: {
            'can_update': roles.observer.canUpdate,
            'can_delete': roles.observer.canDelete,
            'can_complete': roles.observer.canComplete,
        },
    };
};

const mapTabs = (tabs?: TDealTabs): Tabs => {
    if (!tabs) {
        return DEFAULT_TABS;
    }

    return {
        board: {
            outdated: tabs.board?.outdated ?? [],
            'in_progress': tabs.board?.inProgress ?? [],
            today: tabs.board?.today ?? [],
            'seven_days': tabs.board?.sevenDays ?? [],
            'no_term': tabs.board?.noTerm ?? [],
        },
        list: Object.entries(tabs.list ?? {}).reduce<Tabs['list']>((acc, [key, value]) => {
            acc[key === 'noTerm' ? 'no_term' : key] = value;

            return acc;
        }, {}),
    };
};

export const mapDealTasksDataToTasksInformation = (
    tasksData?: TDealTasksData
): TasksInformationSchema => {
    const employees = tasksData?.employees?.map(mapEmployee) ?? [];
    const projects = tasksData?.projects?.map(mapProject) ?? [];

    return {
        employees,
        projects,
        projectsForFilter: projects,
        tags: tasksData?.tags ?? [],
        roles: mapRoles(tasksData?.roles),
        tabs: mapTabs(tasksData?.tabs),
        filteredTabs: null,
        isFiltered: false,
        isNeedFetchGant: true,
        isSorted: false,
        isShowCompleted: false,
        isCollapsedTabsInList: false,
        isExpandedTabsInList: false,
        executorsForFilter: employees,
    };
};
